import { createFeedbackSource, type ConfirmMessage } from '@inform-appshell/mui-appshell';
import type {
  FrameAdapter,
  MessageBoxMessage,
  NotificationTypes,
  ToastTypes,
} from '@inform-appshell/fusion-kit-contracts';

const TONE_MAP = {
  success: 'success',
  error: 'danger',
  warning: 'warning',
  info: 'primary',
} as const;

// Errors and warnings stay up until dismissed; the rest self-dismiss.
const STICKY_TYPES = new Set<NotificationTypes>(['error', 'warning']);
const TOAST_DURATION_MS = 3000;

/**
 * `ConfirmState.variant` is derived from the messages when omitted, but only an `error` message
 * yields `danger`. CUAM's Confirmation Dialog Convention passes `type: 'warning'` for destructive
 * actions, so every confirm would otherwise render as `default`. Set it here, at the boundary that
 * knows the convention.
 */
function toVariant(messages: MessageBoxMessage[]) {
  return messages.some((m) => m.type === 'error' || m.type === 'warning') ? ('danger' as const) : undefined;
}

/**
 * The message `type` is deliberately **not** forwarded. `FeedbackHost` renders any message carrying
 * one as an outlined Alert with its own severity icon, while the danger variant already puts a
 * warning glyph on the title — so a standard CUAM confirm came out with two icons and its message
 * boxed inside the dialog. The severity still does its job here, choosing the variant.
 */
function toMessages(messages: MessageBoxMessage[]): ConfirmMessage[] {
  return messages.map((m) => ({ message: m.message }));
}

/**
 * Bridges fusion-kit's `FrameAdapter` onto mui-appshell's feedback contract.
 *
 * The source queues anything emitted before `FeedbackHost` connects, which is what CUAM used to drop:
 * the adapter is registered during bootstrap, while the host connects from an effect.
 */
export class ShellFrameAdapter implements FrameAdapter {
  readonly source = createFeedbackSource();

  showNotification(message: string | undefined, notificationType: NotificationTypes): void {
    this.source.toast({
      message: message ?? '',
      tone: TONE_MAP[notificationType],
      duration: STICKY_TYPES.has(notificationType) ? Infinity : TOAST_DURATION_MS,
    });
  }

  showNotifications(notifications: { message: string | undefined; notificationType: NotificationTypes }[]): void {
    const last = notifications[notifications.length - 1];
    if (last) this.showNotification(last.message, last.notificationType);
  }

  showToast(message: string, toastType: ToastTypes): void {
    this.showNotification(message, toastType);
  }

  showMessageBox(
    title: string,
    messages: MessageBoxMessage[],
    cancelButtonText: string,
    confirmButtonText: string,
    confirmCallback?: () => void,
    cancelCallback?: () => void
  ): void {
    this.showMessageBoxAsync(title, messages, cancelButtonText, confirmButtonText).then((result) =>
      result === 'confirm' ? confirmCallback?.() : cancelCallback?.()
    );
  }

  showMessageBoxAsync(
    title: string,
    messages: MessageBoxMessage[],
    cancelButtonText: string,
    confirmButtonText: string
  ): Promise<'confirm' | 'cancel'> {
    return this.source.confirm({
      title,
      messages: toMessages(messages),
      cancelButtonText,
      confirmButtonText,
      variant: toVariant(messages),
    });
  }
}
